import { useEffect, useState } from 'react'
import { SparkyAnim } from '@/components/SparkyAnim'

const SLEEP_AFTER_MS = 45_000

/** The one teaser of a session shows shortly after load, then hides again. */
const TEASER_FIRST_MS = 12_000
const TEASER_VISIBLE_MS = 9_000

/** sessionStorage: a teaser was shown, or Sparky opened, in this session. */
const TEASER_SEEN_KEY = 'vonk.sparky.teaserSeen'
/** localStorage: which tip the next session shows. */
const TEASER_NEXT_KEY = 'vonk.sparky.teaserNext'

/** Rotating discovery tips, one per session (Dutch UI). */
const TEASER_MESSAGES = [
  'Hoi! Ik ben Sparky. Stel me een vraag over je deals.',
  'Wil je een samenvatting van een dossier? Vraag het mij!',
  'Ik kan een gap-analyse of DD-verificatie voor je starten.',
  'Vraag me welke deals te lang in een fase blijven hangen.',
  'Ik help je met het opstellen van een IC-package.',
]

function mediaMatches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches
}

/**
 * spec-65 U-029: at most one teaser per session, never on a phone (below 768
 * px) and never with reduced motion. Blocked storage counts as "seen": better
 * quiet than a tip on every page load.
 */
function teaserAllowed(): boolean {
  if (typeof window.matchMedia === 'function' && !mediaMatches('(min-width: 768px)')) return false
  if (mediaMatches('(prefers-reduced-motion: reduce)')) return false
  try {
    return window.sessionStorage.getItem(TEASER_SEEN_KEY) === null
  } catch {
    return false
  }
}

/** Remember that this session had its teaser; false when storage refuses the write. */
function markTeaserSeen(): boolean {
  try {
    window.sessionStorage.setItem(TEASER_SEEN_KEY, '1')
    return true
  } catch {
    return false
  }
}

/** The tip for this session; the next session gets the next one. */
function takeTeaserIndex(): number {
  try {
    const stored = Number(window.localStorage.getItem(TEASER_NEXT_KEY) ?? '0')
    const index = Number.isInteger(stored) && stored >= 0 ? stored % TEASER_MESSAGES.length : 0
    window.localStorage.setItem(TEASER_NEXT_KEY, String((index + 1) % TEASER_MESSAGES.length))
    return index
  } catch {
    return 0
  }
}

/**
 * The floating "Vraag Sparky" action button (spec-29 §8) — bottom-right on
 * every admin/team page. Rendering is guarded by mounting `SparkyHost` inside
 * the admin/team `RequireRole` boundary, so founders never see it.
 *
 * Sparky idles, greets on hover/focus, and dozes off after a while without
 * attention — hovering wakes him up. spec-65 U-029: he is 72 px (88 px from md,
 * set in `sparky-anim.css` under `.sparky-fab`; the flame fills 74% of that) and
 * shows at most one teaser per session — never on a phone or with reduced
 * motion.
 */
export function SparkyButton({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = useState(false)
  const [asleep, setAsleep] = useState(false)
  // Index into TEASER_MESSAGES while a teaser bubble is visible, else null.
  const [teaser, setTeaser] = useState<number | null>(null)
  const [engaged, setEngaged] = useState(false)

  useEffect(() => {
    if (hovered) return
    const timer = window.setTimeout(() => setAsleep(true), SLEEP_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [hovered])

  useEffect(() => {
    if (engaged || !teaserAllowed()) return
    let hideTimer: number | undefined
    const showTimer = window.setTimeout(() => {
      // A teaser that cannot be recorded would come back on every page load.
      if (!markTeaserSeen()) return
      setTeaser(takeTeaserIndex())
      setAsleep(false)
      hideTimer = window.setTimeout(() => setTeaser(null), TEASER_VISIBLE_MS)
    }, TEASER_FIRST_MS)
    return () => {
      window.clearTimeout(showTimer)
      window.clearTimeout(hideTimer)
    }
  }, [engaged])

  const wake = () => {
    setHovered(true)
    setAsleep(false)
  }

  const open = () => {
    markTeaserSeen()
    setEngaged(true)
    setTeaser(null)
    onClick()
  }

  return (
    <div className="sparky-fab fixed bottom-3 right-3 z-40 flex flex-col items-end">
      {teaser !== null && !engaged ? (
        <button
          type="button"
          className="mb-1 mr-2 max-w-64 cursor-pointer rounded-2xl rounded-br-sm border border-border bg-background px-3.5 py-2.5 text-left text-sm text-foreground shadow-lg animate-in fade-in slide-in-from-bottom-2"
          onClick={open}
          data-testid="sparky-teaser"
        >
          {TEASER_MESSAGES[teaser]}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Vraag Sparky"
        title="Vraag Sparky"
        className="rounded-lg"
        onClick={open}
        onMouseEnter={wake}
        onMouseLeave={() => setHovered(false)}
        onFocus={wake}
        onBlur={() => setHovered(false)}
      >
        <SparkyAnim state={hovered || teaser !== null ? 'greeting' : asleep ? 'sleeping' : 'idle'} />
      </button>
    </div>
  )
}
